/**
 * BelloBliss cart drawer behaviour (loaded on every page from theme.liquid).
 *
 * - Opens and refreshes Horizon's cart drawer after any add-to-cart, including
 *   the custom builders that still fire `upcart:open`.
 * - Cart rules, checked after every cart change on every page (drawer and /cart):
 *     1. Subscription Buy 2 Get 1 bottles (Recharge "bogo" plan) must come in
 *        multiples of 3. Otherwise all of them and the subscription gift are removed.
 *     2. The subscription gift is removed when no bogo bottles remain, and capped at 1.
 *     3. Spend gifts are added/removed at their thresholds (only when switched on
 *        in theme settings, after the matching discounts exist).
 * - Drawer buttons: quick add, remove bogo set, upgrade to subscription,
 *   package protection info, protected / plain checkout.
 *
 * Money is never calculated or formatted here; all prices are rendered by Liquid.
 *
 * Nov 2026 speed pass: the rules reuse the cart Shopify just returned (no extra read),
 * and a gift change asks for the re-rendered drawer in the same request, so the drawer
 * updates once instead of twice. The rules themselves are unchanged.
 */
import { CartUpdateEvent } from '@theme/events';

const CFG = window.BBCartConfig || {};
const ROOT = CFG.root || '/';
const INTERNAL = 'X-BB-Cart';
const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const nativeFetch = window.fetch.bind(window);

const toId = (v) => String(v || '');
const sum = (items) => items.reduce((n, i) => n + i.quantity, 0);
const gift = (item) => (item.properties && item.properties._gift) || '';
const isFullCart = (c) => !!(c && Array.isArray(c.items) && c.token);

async function api(path, body) {
  const res = await nativeFetch(ROOT + path.replace(/^\//, ''), {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', [INTERNAL]: '1' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw data;
  return data;
}
const getCart = () => api('cart.js');

/* ---------- cart classification ---------- */
const isBogo = (i) =>
  !gift(i) && i.selling_plan_allocation && (CFG.bogoPlans || []).includes(toId(i.selling_plan_allocation.selling_plan.id));
const isSubGift = (i) => gift(i) === 'true' && toId(i.variant_id) === toId(CFG.subGiftVariant);
const isProtection = (i) => toId(i.variant_id) === toId(CFG.protectionVariant);
const isOneTimeMag = (i) => !gift(i) && !i.selling_plan_allocation && !!(CFG.planByVariant || {})[toId(i.variant_id)];

/* ---------- rules ---------- */
function planChanges(cart) {
  const updates = {};
  const adds = [];
  const bogo = cart.items.filter(isBogo);
  const bogoQty = sum(bogo);
  const subGifts = cart.items.filter(isSubGift);

  if (bogoQty % 3 !== 0) {
    bogo.forEach((i) => (updates[i.key] = 0));
    subGifts.forEach((i) => (updates[i.key] = 0));
  } else if (bogoQty === 0) {
    subGifts.forEach((i) => (updates[i.key] = 0));
  } else if (sum(subGifts) > 1) {
    subGifts.forEach((i, n) => (updates[i.key] = n === 0 ? 1 : 0));
  }

  if (CFG.autoGifts && Array.isArray(CFG.gifts)) {
    const declined = String((cart.attributes || {})._bb_declined_gifts || '').split(',').filter(Boolean);
    const spend = cart.items
      .filter((i) => !gift(i) && !isProtection(i))
      .reduce((n, i) => n + i.final_line_price, 0);
    CFG.gifts.forEach((g) => {
      if (!g || !g.variant) return;
      const lines = cart.items.filter((i) => gift(i) === g.tag);
      // A gift variant added any other way (product page, quick add) is removed.
      cart.items
        .filter((i) => toId(i.variant_id) === toId(g.variant) && !gift(i))
        .forEach((i) => (updates[i.key] = 0));
      const eligible = spend >= g.threshold;
      if (eligible && lines.length === 0 && g.available && !declined.includes(g.tag)) {
        adds.push({ id: g.variant, quantity: 1, properties: { _gift: g.tag } });
      } else if (!eligible) {
        lines.forEach((i) => (updates[i.key] = 0));
      } else if (sum(lines) > 1) {
        lines.forEach((i, n) => (updates[i.key] = n === 0 ? 1 : 0));
      }
    });
  }
  return { updates, adds };
}

// Run the rules one at a time. Apps like Kaching add items in several quick calls; overlapping
// runs could each add the same gift (2 pouches). A queued run always re-reads the cart.
let enforceQueue = Promise.resolve();
let enforceWaiting = 0;
function enforce(cart, opts) {
  const start = enforceWaiting > 0 ? null : cart;
  enforceWaiting++;
  const job = enforceQueue.then(() => enforceNow(start, opts)).finally(() => { enforceWaiting--; });
  enforceQueue = job.catch(() => {});
  return job;
}

// With { render: true } the last cart write also returns the re-rendered drawer (`sections`),
// so the caller can update the drawer once, without a separate fetch.
async function enforceNow(cart, { render = false } = {}) {
  let current = isFullCart(cart) ? cart : await getCart();
  let changed = false;
  let sections = null;
  const sid = render ? drawerSectionId() : null;
  const withSections = (body) => (sid ? { ...body, sections: sid, sections_url: window.location.pathname } : body);

  for (let pass = 0; pass < 3; pass++) {
    const { updates, adds } = planChanges(current);
    const hasUpdates = Object.keys(updates).length > 0;
    if (!hasUpdates && !adds.length) break;
    try {
      if (hasUpdates) {
        // update.js returns the full cart, so the next pass needs no extra read.
        const res = await api('cart/update.js', adds.length ? { updates } : withSections({ updates }));
        sections = res.sections || null;
        current = res;
        changed = true;
      }
      if (adds.length) {
        const res = await api('cart/add.js', withSections({ items: adds })).catch((e) => {
          console.warn('[bb-cart] gift add failed', e);
          return null;
        });
        if (res) {
          sections = res.sections || null;
          changed = true;
        }
        // add.js only returns the added line: read the cart once to check the result.
        current = await getCart();
      }
    } catch (e) {
      // Line keys go stale when the cart changes underneath us: re-read it and try again.
      console.warn('[bb-cart] rule update retried', e);
      sections = null;
      current = await getCart();
    }
  }
  return { cart: current, changed, sections };
}

// Show the result of a rules run: one drawer update using the HTML from the write if we have it.
async function showResult(result) {
  if (!result || !result.changed) return false;
  if (result.sections) applySections(result.sections, result.cart);
  else await refresh(result.cart);
  return true;
}

/* ---------- drawer refresh ---------- */
function drawerSectionId() {
  const el = document.querySelector('cart-items-component[data-drawer]');
  return el ? el.dataset.sectionId : null;
}

function drawerDialog() {
  return document.querySelector('cart-drawer-component dialog');
}

// A drawer re-render can leave the dialog with `open` set but outside the top layer,
// so it renders as a thin strip inside the header. Close it so it can be re-opened as a modal.
function resetStuckDialog() {
  const dialog = drawerDialog();
  if (dialog && dialog.open && !dialog.matches(':modal')) {
    dialog.close();
    return true;
  }
  return false;
}

function openDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.open !== 'function') return;
  resetStuckDialog();
  const dialog = drawerDialog();
  if (dialog && dialog.open) return;
  drawer.open();
}

// After any re-render, put a stuck drawer back as a proper modal.
// Fixed in the same frame (no close / re-open animation, so no flash), with a next-frame backup.
function repairDrawer() {
  const fix = () => {
    const dialog = drawerDialog();
    if (dialog && dialog.open && !dialog.matches(':modal')) {
      dialog.close();
      dialog.showModal();
    }
  };
  fix();
  requestAnimationFrame(fix);
}

function setLoading(on) {
  const el = document.querySelector('cart-items-component[data-drawer]');
  if (el) el.classList.toggle('bb-loading', on);
}

function fetchSections() {
  const sid = drawerSectionId();
  if (!sid) return Promise.resolve({});
  return nativeFetch(`${window.location.pathname}?sections=${encodeURIComponent(sid)}`, {
    headers: { [INTERNAL]: '1' },
  })
    .then((r) => r.json())
    .catch(() => ({}));
}

// Cart count straight from the freshly rendered drawer HTML (saves a /cart.js round trip).
function countFromSections(sections) {
  const html = Object.values(sections || {})[0];
  if (!html) return null;
  const match = html.match(/ref="cartItemCount"[^>]*>\s*(\d+)/);
  return match ? Number(match[1]) : null;
}

// Always pass a full cart where possible: without one, every product form on the page
// fetches /cart.js itself to update its "(1)" count (20+ requests on BF26).
function applySections(sections, cart) {
  const full = isFullCart(cart) ? cart : null;
  const count = full ? full.item_count : countFromSections(sections);
  document.dispatchEvent(
    new CartUpdateEvent(full || {}, 'bb-cart', { itemCount: count ?? 0, source: 'bb-cart', sections: sections || {} })
  );
  setLoading(false);
  repairDrawer();
}

// Re-render the drawer. Cart and drawer HTML are fetched in parallel. Opens once the content is in.
async function refresh(cart, { open = false } = {}) {
  setLoading(true);
  try {
    const [current, sections] = await Promise.all([isFullCart(cart) ? Promise.resolve(cart) : getCart(), fetchSections()]);
    applySections(sections, current);
  } finally {
    setLoading(false);
    if (open) requestAnimationFrame(openDrawer);
  }
}

// Run the cart rules in the background; only re-render if they changed something.
function enforceInBackground(cart) {
  enforce(cart, { render: true })
    .then(showResult)
    .catch((e) => console.error('[bb-cart]', e));
}

// Cart write that returns the re-rendered drawer in the same response. Returns the full cart.
async function writeAndRender(path, body) {
  const sid = drawerSectionId();
  const payload = sid ? { ...body, sections: sid, sections_url: window.location.pathname } : body;
  setLoading(true);
  try {
    const res = await api(path, payload);
    // Add responses aren't a full cart. Fetch it once here, otherwise every product form on the page fetches it itself.
    const cart = isFullCart(res) ? res : await getCart();
    if (res && res.sections) applySections(res.sections, cart);
    else await refresh(cart);
    return cart;
  } finally {
    setLoading(false);
  }
}

/* ---------- debounced runner for changes made elsewhere ---------- */
let timer = null;
let wantOpen = false;
let wantRefresh = false;
let pendingCart = null;

// `cart` is the full cart from the response that triggered this (if any); the latest call wins.
function schedule({ open = false, refresh: doRefresh = true, cart = null } = {}) {
  wantOpen = wantOpen || open;
  wantRefresh = wantRefresh || doRefresh;
  pendingCart = isFullCart(cart) ? cart : null;
  clearTimeout(timer);
  timer = setTimeout(run, 30);
}

async function run() {
  const open = wantOpen;
  const doRefresh = wantRefresh;
  const given = pendingCart;
  wantOpen = wantRefresh = false;
  pendingCart = null;
  try {
    if (doRefresh) {
      setLoading(true);
      // Cart (for the rules) and drawer HTML at the same time. Show what they added straight away,
      // then let the rules adjust gifts; the drawer only updates again if a rule changed something.
      const [cart, sections] = await Promise.all([given ? Promise.resolve(given) : getCart(), fetchSections()]);
      applySections(sections, cart);
      if (open) requestAnimationFrame(openDrawer);
      await showResult(await enforce(cart, { render: true }));
    } else {
      // The theme already re-rendered the drawer: only update it again if a rule changed the cart.
      await showResult(await enforce(given, { render: true }));
    }
  } catch (e) {
    console.error('[bb-cart]', e);
    setLoading(false);
  }
  // Open only after the new content is in, so the re-render can't knock the drawer out of modal mode.
  if (open) requestAnimationFrame(openDrawer);
}

/* ---------- arriving from the /cart page redirect (see bb-cart-config) ---------- */
if (window.location.hash === '#bb-cart') {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  customElements.whenDefined('cart-drawer-component').then(() => schedule({ open: true, refresh: true }));
}

/* ---------- product forms that would otherwise post to /cart ---------- */
// Native Shopify product forms (shower steamer buy box, magnesium bundle buy box, Kaching)
// post to /cart/add and land on the cart page. UpCart used to intercept these; now we do.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  const action = form.getAttribute('action') || '';
  if ((form.method || '').toLowerCase() !== 'post' || !/\/cart\/add(\.js)?(\?|$)/.test(action)) return;

  let data;
  try {
    data = event.submitter ? new FormData(form, event.submitter) : new FormData(form);
  } catch (e) {
    data = new FormData(form);
  }
  // Leave "straight to checkout" forms alone.
  if (String(data.get('return_to') || '').includes('checkout')) return;

  event.preventDefault();
  const button = event.submitter || form.querySelector('[type="submit"]');
  setBusy(button, true);
  if (button) button.classList.add('loading');
  const errorEl = form.querySelector('.bb-form-error');
  if (errorEl) errorEl.remove();

  try {
    const res = await nativeFetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', [INTERNAL]: '1' },
      body: data,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw body;
    schedule({ open: true, refresh: true });
  } catch (e) {
    setLoading(false);
    const msg = (e && (e.description || e.message)) || 'Sorry, something went wrong. Please try again.';
    const p = document.createElement('p');
    p.className = 'bb-form-error';
    p.setAttribute('role', 'alert');
    p.textContent = msg;
    (button && button.parentNode ? button.parentNode : form).appendChild(p);
    console.error('[bb-cart] form add failed', e);
  } finally {
    setBusy(button, false);
    if (button) button.classList.remove('loading');
  }
});

/* ---------- watch cart writes from apps, builders and the theme ---------- */
function headerValue(headers, name) {
  if (!headers) return null;
  if (typeof headers.get === 'function') return headers.get(name);
  return headers[name] || null;
}

window.fetch = async function (input, init) {
  const res = await nativeFetch(input, init);
  try {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const match = url.match(CART_URL_RE);
    if (match && res.ok && !headerValue(init && init.headers, INTERNAL)) {
      const body = init && init.body;
      // Horizon's own forms ask for `sections` and re-render the drawer themselves.
      const themeHandled =
        (typeof body === 'string' && body.includes('"sections"')) ||
        (typeof FormData !== 'undefined' && body instanceof FormData && body.has('sections'));
      const opts = { open: match[1] === 'add' && !themeHandled, refresh: !themeHandled };
      // change / update / clear responses are the full cart: hand it to the rules instead of
      // reading the cart again. Read from a copy, without holding up the caller.
      if (match[1] === 'add') schedule(opts);
      else
        res
          .clone()
          .json()
          .then((cart) => schedule({ ...opts, cart }))
          .catch(() => schedule(opts));
    }
  } catch (e) {
    /* never break the caller */
  }
  return res;
};

const xhrOpen = XMLHttpRequest.prototype.open;
const xhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.open = function (method, url) {
  this.__bbUrl = String(url || '');
  return xhrOpen.apply(this, arguments);
};
XMLHttpRequest.prototype.send = function () {
  const match = (this.__bbUrl || '').match(CART_URL_RE);
  if (match) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) schedule({ open: match[1] === 'add', refresh: true });
    });
  }
  return xhrSend.apply(this, arguments);
};

// The magnesium builders and steamer buy box still announce adds this way.
document.addEventListener('upcart:open', () => schedule({ open: true, refresh: true }));

// Any other re-render of the drawer (Horizon's own quantity / remove buttons) gets the same repair.
document.addEventListener('cart:update', repairDrawer);

/* ---------- drawer buttons ---------- */
function setBusy(el, busy) {
  if (!el) return;
  el.disabled = busy;
  el.toggleAttribute('aria-busy', busy);
}

async function quickAdd(btn) {
  setBusy(btn, true);
  try {
    const cart = await writeAndRender('cart/add.js', { items: [{ id: Number(btn.dataset.bbAdd), quantity: 1 }] });
    enforceInBackground(cart);
  } catch (e) {
    console.error('[bb-cart] add failed', e);
    setBusy(btn, false);
  }
}

async function removeBogoSet(btn) {
  setBusy(btn, true);
  try {
    let cart = await getCart();
    const updates = {};
    cart.items.filter((i) => isBogo(i) || isSubGift(i)).forEach((i) => (updates[i.key] = 0));
    if (Object.keys(updates).length) cart = await writeAndRender('cart/update.js', { updates });
    enforceInBackground(cart);
  } catch (e) {
    console.error('[bb-cart] remove failed', e);
    setBusy(btn, false);
  }
}

async function removeGift(btn) {
  setBusy(btn, true);
  try {
    const tag = btn.dataset.bbGiftTag || '';
    const body = { updates: { [btn.dataset.bbRemoveGift]: 0 } };
    // Remember a declined spend gift so auto-add doesn't put it straight back.
    if (tag.startsWith('spend-')) {
      const cart = await getCart();
      const declined = String((cart.attributes || {})._bb_declined_gifts || '').split(',').filter(Boolean);
      if (!declined.includes(tag)) declined.push(tag);
      body.attributes = { _bb_declined_gifts: declined.join(',') };
    }
    await writeAndRender('cart/update.js', body);
  } catch (e) {
    console.error('[bb-cart] gift remove failed', e);
    setBusy(btn, false);
  }
}

async function upgradeToSubscription(btn) {
  setBusy(btn, true);
  try {
    const single = btn.dataset.bbUpgrade === 'single';
    const plans = single ? CFG.singlePlanByVariant || {} : CFG.planByVariant;
    let cart = await getCart();
    if (!single && sum(cart.items.filter(isOneTimeMag)) !== 3) throw new Error('Upgrade needs exactly 3 one-time bottles');
    // Line keys change after every edit (discounts are re-evaluated), so re-read the cart before each change.
    for (let n = 0; n < 8; n++) {
      const line = cart.items.find(isOneTimeMag);
      if (!line) break;
      const plan = plans[toId(line.variant_id)];
      if (!plan) throw new Error('Missing subscription plan ID');
      cart = await api('cart/change.js', { id: line.key, quantity: line.quantity, selling_plan: Number(plan) });
    }
    // The mystery steamers gift only comes with the Buy 2 Get 1 subscription.
    if (!single && CFG.subGiftVariant && !cart.items.some(isSubGift)) {
      await api('cart/add.js', {
        items: [{ id: Number(CFG.subGiftVariant), quantity: 1, properties: { _gift: 'true' } }],
      }).catch((e) => console.warn('[bb-cart] subscription gift add failed', e));
      cart = null;
    }
    const res = await enforce(cart);
    await refresh(res.cart);
  } catch (e) {
    console.error('[bb-cart] upgrade failed', e);
    const res = await enforce().catch(() => null);
    await refresh(res && res.cart);
  }
}

async function checkout(btn, event) {
  event.preventDefault();
  setBusy(btn, true);
  const wantProtection = btn.dataset.bbCheckout === 'protected' && CFG.protectionVariant;
  try {
    // Final gift check before checkout, always against a fresh read of the cart.
    const checked = await enforce().catch(() => null);
    const cart = checked && isFullCart(checked.cart) ? checked.cart : await getCart();
    const lines = cart.items.filter(isProtection);
    const updates = {};
    if (wantProtection) {
      if (!lines.length) {
        await api('cart/add.js', { items: [{ id: Number(CFG.protectionVariant), quantity: 1 }] });
      } else {
        lines.forEach((i, n) => (updates[i.key] = n === 0 ? 1 : 0));
      }
    } else {
      lines.forEach((i) => (updates[i.key] = 0));
    }
    if (Object.keys(updates).length) await api('cart/update.js', { updates });
  } catch (e) {
    // Never block a sale over protection: carry on to checkout.
    console.error('[bb-cart] protection update failed', e);
  }
  window.location.href = ROOT + 'checkout';
}

function toggleInfo(btn) {
  const panel = document.getElementById(btn.getAttribute('aria-controls'));
  if (!panel) return;
  panel.hidden = !panel.hidden;
  document
    .querySelectorAll(`[data-bb-info][aria-controls="${panel.id}"]`)
    .forEach((b) => b.setAttribute('aria-expanded', String(!panel.hidden)));
}

function updateArrows(track) {
  const wrap = track && track.closest('.bb-ups');
  if (!wrap) return;
  const prev = wrap.querySelector('[data-bb-scroll="-1"]');
  const next = wrap.querySelector('[data-bb-scroll="1"]');
  if (prev) prev.disabled = track.scrollLeft < 4;
  if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest(
    '[data-bb-add],[data-bb-remove-bogo],[data-bb-remove-gift],[data-bb-upgrade],[data-bb-info],[data-bb-checkout],[data-bb-scroll]'
  );
  if (!btn || btn.disabled) return;
  if (btn.hasAttribute('data-bb-remove-gift')) return removeGift(btn);
  if (btn.hasAttribute('data-bb-add')) return quickAdd(btn);
  if (btn.hasAttribute('data-bb-remove-bogo')) return removeBogoSet(btn);
  if (btn.hasAttribute('data-bb-upgrade')) return upgradeToSubscription(btn);
  if (btn.hasAttribute('data-bb-info')) return toggleInfo(btn);
  if (btn.hasAttribute('data-bb-checkout')) return checkout(btn, event);
  if (btn.hasAttribute('data-bb-scroll')) {
    const track = btn.closest('.bb-ups').querySelector('[data-bb-track]');
    track.scrollBy({ left: Number(btn.dataset.bbScroll) * track.clientWidth * 0.7, behavior: 'smooth' });
  }
});

let drag = null;
document.addEventListener('pointerdown', (event) => {
  const track = event.target.closest && event.target.closest('[data-bb-track]');
  if (!track || event.pointerType !== 'mouse' || event.button !== 0) return;
  drag = { track, x: event.clientX, left: track.scrollLeft, moved: false };
});
document.addEventListener('pointermove', (event) => {
  if (!drag) return;
  const dx = event.clientX - drag.x;
  if (!drag.moved && Math.abs(dx) > 4) {
    drag.moved = true;
    drag.track.classList.add('is-dragging');
  }
  if (drag.moved) drag.track.scrollLeft = drag.left - dx;
});
document.addEventListener('pointerup', () => {
  if (!drag) return;
  const { track, moved } = drag;
  drag = null;
  track.classList.remove('is-dragging');
  if (moved) {
    // Swallow the click that ends a drag so it doesn't trigger Quick Add.
    const swallow = (e) => { e.stopPropagation(); e.preventDefault(); };
    track.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
  }
});

document.addEventListener(
  'scroll',
  (event) => {
    if (event.target instanceof Element && event.target.matches('[data-bb-track]')) updateArrows(event.target);
  },
  true
);
document.addEventListener('cart:update', () => {
  requestAnimationFrame(() => document.querySelectorAll('[data-bb-track]').forEach(updateArrows));
});

/* ---------- no flash when the last item is removed ---------- */
// Horizon swaps in an "empty cart" template with a page-wide animation when the last item is removed,
// which briefly hides the drawer. Without the template it waits for the real empty drawer instead.
function disableEmptyTemplate() {
  const tpl = document.getElementById('empty-cart-template');
  if (tpl) tpl.id = 'bb-empty-cart-template';
}
disableEmptyTemplate();
document.addEventListener('cart:update', disableEmptyTemplate);
document.addEventListener('pointerdown', disableEmptyTemplate, true);
document.addEventListener('click', disableEmptyTemplate, true);
document.addEventListener('change', disableEmptyTemplate, true);

// Check the cart rules once on every page load (catches carts edited on /cart or before launch).
schedule({ refresh: false });